import { DisplayAmount } from "components/display/amount";
import React from "react";
import { EvtStatsCard as StatsCard } from "components/stats/display-card";
import { EvtStatsCardLoader } from "components/stats/loader";
import { useBillableStats } from "../hooks/useBillablestats";
import {
  useCollectedStats,
  useDueStats,
} from "@/modules/reports/billables/status/hooks/useDateBasedStats";
import { getExpectedBillingInfo, getCollectedBillingInfo } from "../constant";

export function StatsSection({ status, filters }) {
  const { data: stats, isLoading: isStatsLoading } = useBillableStats(
    status,
    filters
  );

  const { data: dueStats, isLoading: isDueStatsLoading } = useDueStats(
    filters,
    status
  );

  const { data: collectedStats, isLoading: isCollectedStatsLoading } =
    useCollectedStats(filters, status);

  const getIsLoading = () => {
    switch (status) {
      case "billed":
        return isStatsLoading || isDueStatsLoading;
      case "collected":
        return isStatsLoading || isCollectedStatsLoading;
      default:
        return isStatsLoading;
    }
  };

  const isLoading = getIsLoading();

  const renderLoadingStateByStatus = () => {
    switch (status) {
      case "contracted":
      case "pending_invoice":
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <EvtStatsCardLoader />
            <EvtStatsCardLoader />
            <EvtStatsCardLoader />
          </div>
        );

      case "billed":
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 2xl:grid-cols-5 gap-4">
            <EvtStatsCardLoader />
            <EvtStatsCardLoader />
            <EvtStatsCardLoader />
            <EvtStatsCardLoader />
            <EvtStatsCardLoader />
          </div>
        );

      case "collected":
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <EvtStatsCardLoader />
            <EvtStatsCardLoader />
            <EvtStatsCardLoader />
          </div>
        );

      case "cancelled":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EvtStatsCardLoader />
            <EvtStatsCardLoader />
          </div>
        );

      case "all":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EvtStatsCardLoader />
          </div>
        );

      default:
        return null;
    }
  };

  const renderStatsByStatus = () => {
    switch (status) {
      case "contracted":
      case "pending_invoice":
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            <StatsCard
              label="Expected Billing"
              value={<DisplayAmount value={stats.expectedBilling} />}
              info={getExpectedBillingInfo(status).info}
            />
            <StatsCard label="Delayed Count" value={stats.overdueCount} />
            <StatsCard
              label="Delayed Amount"
              value={<DisplayAmount value={stats.overdueAmount} />}
            />
          </div>
        );

      case "billed":
        return (
          <div className="grid grid-cols-1 md:grid-cols-3  2xl:grid-cols-5 gap-4">
            <StatsCard
              label="Expected Billing"
              value={<DisplayAmount value={stats.expectedBilling} />}
              info={getExpectedBillingInfo(status).info}
            />
            <StatsCard
              label="Overdue Amount"
              value={<DisplayAmount value={stats.overdueAmount} />}
            />
            <StatsCard
              label="Due Today"
              value={<DisplayAmount value={dueStats?.dueToday || 0} />}
            />
            <StatsCard
              label="Due (Next 15 Days)"
              value={<DisplayAmount value={dueStats?.dueNext15Days || 0} />}
            />
            <StatsCard
              label="Due (Next 30 Days)"
              value={<DisplayAmount value={dueStats?.dueNext30Days || 0} />}
            />
          </div>
        );

      case "collected":
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatsCard
              label="Collected Billing"
              value={<DisplayAmount value={stats.collectedBilling} />}
              info={getCollectedBillingInfo(status).info}
            />
            <StatsCard
              label="Collected (Last 15 Days)"
              value={
                <DisplayAmount
                  value={collectedStats?.collectedLast15Days || 0}
                />
              }
            />
            <StatsCard
              label="Collected (Last 30 Days)"
              value={
                <DisplayAmount
                  value={collectedStats?.collectedLast30Days || 0}
                />
              }
            />
          </div>
        );

      case "cancelled":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatsCard label="Cancelled Count" value={stats.count} />
            <StatsCard
              label="Cancelled Billing"
              value={<DisplayAmount value={stats.totalAmount} />}
            />
          </div>
        );

      case "all":
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            <StatsCard
              label="Expected Billing"
              value={<DisplayAmount value={stats.expectedBilling} />}
              info={getExpectedBillingInfo(status).info}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-4 md:p-6">
      {isLoading ? renderLoadingStateByStatus() : renderStatsByStatus()}
    </div>
  );
}
